import { TxStatus } from "@aztec/aztec.js";
import { OutboxAbi } from "@aztec/l1-artifacts";
import type { L2ToL1MembershipWitness } from "@aztec/stdlib/messaging";
import { type Hex, L1Portal, L1Token } from "@turnstile-portal/turnstile.js";
import { type Address, getAddress, getContract } from "viem";
import { getL2ClientAsync, node } from "../aztec-utils";
import { getL1PortalAddr } from "../lib/addresses";
import { getL2Portal, getL2TokenFromL1TokenAddress } from "../lib/singletons";
import { getL1ClientAsync } from "../wagmi";

export async function initiateL2Withdrawal(
  l1TokenAddr: Address,
  l1Recipient: Address,
  amount: bigint,
  startWaiting?: () => void,
) {
  const l2Client = await getL2ClientAsync();
  const l2Portal = await getL2Portal();

  // Create a burn authorization witness for the L2 token.
  // This is required to give the Turnstile Portal permission to burn the tokens.
  const l2Token = await getL2TokenFromL1TokenAddress(l1TokenAddr);
  const l2TokenAddr = l2Token.getAddress();
  console.log(`L2 token address: ${l2TokenAddr}`);

  console.log(`Initiating withdrawal of ${amount} ${await l2Token.getSymbol()} to L1 recipient ${l1Recipient}`);
  console.log("Current L2 balance:", await l2Token.balanceOfPublic(l2Client.getAddress()).simulate({ from: l2Client.getAddress() }));

  // const { action, nonce } = await l2Token.createBurnAction(l2Client.getAddress(), amount);
  // console.log(`Burn action ${action}, nonce ${nonce}`);

  const { action, nonce } = await l2Token.createPublicBurnAuthwitAction(l2Client.getAddress(), amount);

  const burnAuthwitTx = await action.send({ from: l2Client.getAddress(), fee: l2Client.getFeeOpts() });

  // TODO: Waiting for the burn auth tx to be mined will be slow. We should combine the withdraw tx with the burn auth tx.
  // This will require reworking the turnstile.js library a bit to allow it to return un-sent transactions that we can then combine.
  const burnAuthwitReceipt = await burnAuthwitTx.wait();
  console.log(`Burn authwit transaction hash: ${(await burnAuthwitTx.getTxHash()).toString()}`);
  if (burnAuthwitReceipt.status !== TxStatus.SUCCESS) {
    throw new Error("Burn authwit transaction failed");
  }
  console.log("Burn authwit transaction successful");

  if (startWaiting) {
    startWaiting();
    if (import.meta.env.MODE === "development") {
      await new Promise((resolve) => setTimeout(resolve, 10000));
    }
  }

  // Initiate the withdrawal from the Turnstile Portal
  const { tx: withdrawTx, withdrawData } = await l2Portal.withdrawPublic(l1TokenAddr, l1Recipient, amount, nonce, {
    from: l2Client.getAddress(),
    fee: l2Client.getFeeOpts(),
  });

  const txHash = (await withdrawTx.getTxHash()).toString();
  console.log(`Withdrawal initiated on L2. Tx: ${txHash}`);

  const receipt = await withdrawTx.wait();
  if (receipt.status !== TxStatus.SUCCESS) {
    throw new Error("Withdrawal failed");
  }
  const l2BlockNumber = receipt.blockNumber;
  if (!l2BlockNumber) {
    throw new Error("Failed to get block number");
  }

  const l1ContractAddresses = await l2Client.getNode().getL1ContractAddresses();
  const outboxVersion = await l2Portal.getAztecL1OutboxVersion(l1ContractAddresses.outboxAddress);
  const message = await l2Portal.computeL2ToL1Message(l1TokenAddr, l1Recipient, amount, outboxVersion);
  const witness = await l2Portal.getL2ToL1MembershipWitness(l2BlockNumber, message);

  console.log("New L2 balance:", await l2Token.balanceOfPublic(l2Client.getAddress()).simulate({ from: l2Client.getAddress() }));

  return { txHash, l2BlockNumber, witness, message, withdrawData };
}

export async function completeL1Withdrawal(
  l1TokenAddr: Address,
  l2BlockNumber: number,
  witness: L2ToL1MembershipWitness,
  withdrawData: Hex,
) {
  const l1Client = await getL1ClientAsync();

  // Wait for the L2 block to be available on the L1 chain
  const l1Portal = new L1Portal(getAddress(getL1PortalAddr()), l1Client);

  console.log(`Waiting for L2 block ${l2BlockNumber} to be available on L1...`);
  // TODO: this should probably be polled for separately rather than blocking here
  await l1Portal.waitForBlockOnL1(l2BlockNumber, 2000, 60);

  const { siblingPath, leafIndex } = witness;

  const l1Token = new L1Token(l1TokenAddr, l1Client);
  console.log(`Current L1 balance: ${await l1Token.balanceOf(l1Client.getAddress())}`);

  const tx = await l1Portal.withdraw(withdrawData, l2BlockNumber, leafIndex, siblingPath);
  console.log(`L1 Withdraw transaction hash: ${tx}`);

  if (import.meta.env.MODE === "development") {
    await new Promise((resolve) => setTimeout(resolve, 10000));
  }

  const receipt = await l1Client.getPublicClient().waitForTransactionReceipt({ hash: tx });
  if (receipt.status !== TxStatus.SUCCESS) {
    throw new Error(`L1 Withdraw transaction failed: ${receipt.status}`);
  }
  console.log("L1 Withdrawal complete");
  console.log(`New L1 balance: ${await l1Token.balanceOf(l1Client.getAddress())}`);

  return tx;
}

export async function isWithdrawn(l2BlockNumber: number, leafId: bigint) {
  const { outboxAddress } = await node.getL1ContractAddresses();
  const outbox = getContract({
    abi: OutboxAbi,
    address: outboxAddress.toString(),
    client: (await getL1ClientAsync()).getPublicClient()
  });
  return await outbox.read.hasMessageBeenConsumedAtBlock([BigInt(l2BlockNumber), leafId]);
}
